import { useState } from "react";
import { CheckCircle2, Info } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import TombBlessingForm from "@/components/TombBlessingForm";
import TombBlessingLookup from "@/components/TombBlessingLookup";
import { NOV2_MASSES, DONATION_QR, fmtDate, fmtTime, peso } from "@/data/tombBlessing";

const MassReminder = () => (
  <div data-testid="tomb-mass-reminder" className="bg-cocoa-900 text-parchment-100 p-8 md:p-10 relative overflow-hidden">
    <div className="absolute -top-10 -right-6 font-serif text-[11rem] text-gold/10 leading-none select-none">✠</div>
    <div className="relative text-[11px] uppercase tracking-[0.28em] text-gold mb-2">Reminder · Monday</div>
    <h3 className="relative font-serif text-2xl md:text-3xl mb-1">November 2, 2026</h3>
    <p className="relative font-sans font-light text-parchment-200/80 text-sm mb-6">Holy Masses in the cemeteries · All Souls' Day</p>
    <ul className="relative space-y-3">
      {NOV2_MASSES.map((m) => (
        <li key={m.t} className="flex items-baseline gap-5 border-b border-parchment-100/10 pb-3 last:border-0">
          <span className="font-serif text-xl text-gold w-24 shrink-0">{m.t}</span>
          <span className="font-sans text-parchment-100">{m.v}</span>
        </li>
      ))}
    </ul>
  </div>
);

const QrCard = () => (
  <div data-testid="tomb-donation-qr" className="bg-parchment-50 border border-parchment-400 p-6 md:p-8">
    <div className="text-[11px] uppercase tracking-[0.28em] text-sage-dark mb-2">Online Donation</div>
    <h3 className="font-serif text-2xl text-cocoa-900 mb-4">Scan to give</h3>
    <a href={DONATION_QR} target="_blank" rel="noopener noreferrer" data-testid="tomb-donation-qr-link">
      <img src={DONATION_QR} alt="BPI OneQR — St Francis AP Donation" className="w-full max-w-[280px] mx-auto shadow-[0_10px_30px_-18px_rgba(60,40,20,0.5)] hover:scale-[1.02] transition-transform duration-500" />
    </a>
    <p className="mt-4 font-sans text-sm text-cocoa-700 leading-relaxed">
      Accepts any bank or e-wallet via <strong className="font-medium text-cocoa-900">BPI OneQR</strong>. Kindly keep your receipt and mention your reference number. You may also give in person on the day of blessing.
    </p>
  </div>
);

const Confirmation = ({ d, onClose }) => (
  <div data-testid="tomb-confirmation" className="bg-parchment-50 border border-parchment-400 p-10 md:p-14 text-center">
    <CheckCircle2 className="mx-auto text-sage-dark" size={52} strokeWidth={1.2} />
    <h3 className="font-serif text-3xl text-cocoa-900 mt-5">Your request has been <span className="italic text-terracotta">received.</span></h3>
    <p className="mt-3 font-sans text-cocoa-700 max-w-lg mx-auto">Thank you, {d.requester_name}. The Parish Office will confirm the schedule of the blessing.</p>
    <dl className="mt-8 grid sm:grid-cols-2 gap-5 text-left max-w-lg mx-auto">
      {[
        ["Reference", d.id.slice(0, 8).toUpperCase()],
        ["Cemetery", d.cemetery],
        ["Date & Time", `${fmtDate(d.blessing_date)} · ${fmtTime(d.blessing_time)}`],
        ["Donation", peso(d.donation_amount)],
      ].map(([k, v]) => (
        <div key={k}>
          <dt className="text-[10px] uppercase tracking-[0.22em] text-sage-dark">{k}</dt>
          <dd data-testid={`tomb-confirm-${k.toLowerCase().split(" ")[0]}`} className="font-serif text-lg text-cocoa-900 mt-1">{v}</dd>
        </div>
      ))}
    </dl>
    <button onClick={onClose} data-testid="tomb-confirm-new" className="mt-10 border border-cocoa-900/40 text-cocoa-900 px-7 py-3 text-sm font-sans hover:bg-cocoa-900 hover:text-parchment-100 transition-colors">
      Submit another request
    </button>
  </div>
);

export default function TombBlessing() {
  const [done, setDone] = useState(null);
  return (
    <section id="tomb-blessing" data-testid="tomb-blessing-section" className="py-24 md:py-36 bg-parchment-100 grain relative">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel
          kicker="All Saints & All Souls · Oct 31 – Nov 2, 2026"
          title="Blessing of Tombs"
          intro="Request a priest or lay cooperator to bless the resting place of your beloved departed. Blessings are held from 7:00 AM to 10:00 PM."
        />
        <div className="mt-10 max-w-3xl mx-auto bg-parchment-50 border-l-2 border-terracotta p-5 flex gap-3 items-start">
          <Info className="text-terracotta shrink-0 mt-0.5" size={18} strokeWidth={1.4} />
          <p className="font-sans text-sm text-cocoa-800 leading-relaxed">
            Submitting this form does <em className="not-italic font-medium text-terracotta">not</em> automatically confirm the schedule. The Parish Office will verify and confirm the time of blessing. Use your reference number to check the status.
          </p>
        </div>
        <div className="mt-10"><TombBlessingLookup /></div>
        <div className="mt-12 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">
            {done ? <Confirmation d={done} onClose={() => setDone(null)} /> : <TombBlessingForm onDone={setDone} />}
          </div>
          <aside className="lg:col-span-4 space-y-8 lg:sticky lg:top-28">
            <MassReminder />
            <QrCard />
          </aside>
        </div>
      </div>
      <style>{`
        #tomb-blessing .field { width:100%; background:transparent; border:0; border-bottom:1px solid rgba(122,92,79,0.4); padding:10px 2px; font-family:'Figtree',system-ui,sans-serif; color:#3E2723; outline:none; transition:border-color .2s; }
        #tomb-blessing .field:focus { border-bottom-color:#C25953; }
        #tomb-blessing .field::placeholder { color:rgba(62,39,35,0.3); font-style:italic; }
      `}</style>
    </section>
  );
}
